import { SUITS, suitOf } from './cards';
import { pick, type Rng } from './rng';
import type { TrumpMode } from './rules';
import type { GameEvent, HandState, Play, RevealTrigger, Seat, TrumpState } from './types';

/**
 * Trump procedures (PRD §4.2) are explicit, non-mixable presets. Each mode differs only in when trump
 * is established and which plays are eligible; the trick evaluator is shared.
 */
export function needsReserve(mode: TrumpMode): boolean {
  return mode === 'BAND_A_AUTO' || mode === 'BAND_B_REQUEST';
}

export function initialTrump(mode: TrumpMode, dealer: Seat, players: number, rng: Rng): TrumpState {
  const base: TrumpState = {
    mode,
    status: 'NONE',
    suit: null,
    reserverSeat: null,
    cardId: null,
    activatedAt: null,
    requestedBy: null,
  };
  switch (mode) {
    case 'CUT':
      return base;
    case 'BAND_A_AUTO':
    case 'BAND_B_REQUEST':
      return { ...base, status: 'HIDDEN', reserverSeat: (dealer + 1) % players };
    case 'RANDOM_OPEN':
      return {
        ...base,
        status: 'OPEN',
        suit: pick(SUITS, rng),
        activatedAt: { trick: 0, order: 0 },
      };
  }
}

/**
 * Whether a play counts as a trump when the trick is evaluated.
 * - CUT: the cutting card and everything after it.
 * - BAND_A_AUTO: the whole reveal trick is evaluated with the revealed suit (including earlier plays).
 * - BAND_B_REQUEST: only cards played after the reveal.
 * - RANDOM_OPEN: always.
 */
export function isTrumpEligible(trump: TrumpState, trickIndex: number, play: Play): boolean {
  if (trump.suit === null || trump.activatedAt === null) return false;
  if (suitOf(play.cardId) !== trump.suit) return false;
  if (trickIndex > trump.activatedAt.trick) return true;
  if (trickIndex < trump.activatedAt.trick) return false;
  if (trump.mode === 'BAND_A_AUTO') return true;
  return play.order >= trump.activatedAt.order;
}

/** Expose the hidden reserve: it rejoins the reserver's playable hand. Mutates `hand`. */
export function revealReserve(hand: HandState, bySeat: Seat, trigger: RevealTrigger): GameEvent {
  const { trump, reserved } = hand;
  if (trump.status !== 'HIDDEN' || reserved === null || trump.reserverSeat === null) {
    throw new Error('revealReserve called without a hidden reserve');
  }
  const suit = suitOf(reserved);
  hand.trump = {
    ...trump,
    status: 'REVEALED',
    suit,
    cardId: reserved,
    activatedAt: { trick: hand.trickIndex, order: hand.trick.length },
    requestedBy: trigger === 'REQUEST' ? bySeat : null,
  };
  hand.hands[trump.reserverSeat]!.push(reserved);
  hand.reserved = null;
  return {
    t: 'TRUMP_REVEALED',
    reserverSeat: trump.reserverSeat,
    bySeat,
    suit,
    cardId: reserved,
    trigger,
  };
}

/**
 * Reveal checks performed when the turn passes to `seat`, before legal-move validation.
 * Band-A: the first player unable to follow triggers the reveal. Both Band modes apply the PRD's
 * hidden-reserve safety rule: if the reserve is the reserver's only card, or their only card of the
 * led suit, it is revealed so the hand can always finish.
 */
export function revealAtTurnStart(hand: HandState, seat: Seat): GameEvent | null {
  const { trump, reserved } = hand;
  if (trump.status !== 'HIDDEN' || reserved === null) return null;
  const playable = hand.hands[seat]!;
  const isReserver = seat === trump.reserverSeat;
  const ledSuit = hand.trick.length > 0 ? suitOf(hand.trick[0]!.cardId) : null;
  const voidOfLed = ledSuit !== null && !playable.some((c) => suitOf(c) === ledSuit);

  if (isReserver && playable.length === 0) return revealReserve(hand, seat, 'FORCED');

  if (trump.mode === 'BAND_A_AUTO') {
    if (voidOfLed)
      return revealReserve(
        hand,
        seat,
        isReserver && suitOf(reserved) === ledSuit ? 'FORCED' : 'VOID',
      );
    return null;
  }

  if (trump.mode === 'BAND_B_REQUEST' && isReserver && voidOfLed && suitOf(reserved) === ledSuit) {
    return revealReserve(hand, seat, 'FORCED');
  }
  return null;
}

/** BAND_B: a void player asks for the reveal before choosing a card; they must then trump if able. */
export function revealAtTrumpRequest(hand: HandState, seat: Seat): GameEvent {
  return revealReserve(hand, seat, 'REQUEST');
}
